/**
 * Test-only entry point for the browser-driven document tests (see `e2e-web/`).
 *
 * Registered only in a dev build (see `IS_DEV`), and deliberately *not* behind
 * `requireAuth` — the same reason as `FlashBookFixtures`: the reader, and the card
 * an author writes a page in, only exist behind the composer, and a fresh browser
 * has no session to sit in. Everything rendered here is the shipping surface; only
 * the document it opens is built rather than uploaded (`minimalPdf`).
 *
 *   /document-fixtures?fixture=reader
 *     the real full screen reader, over a two-page document nobody wrote on the
 *     way in — the state an author is in when they press a page's `+`
 *   /document-fixtures?fixture=reader&editor=1
 *     the same reader with the page's card already open, which is where an author
 *     who pressed *Add activity* on a page of the card lands
 *   /document-fixtures?fixture=reader&landscape=1
 *     the same reader over a landscape document, which is the shape a page's
 *     thumbnail has to follow rather than the rail's square
 *   /document-fixtures?fixture=post
 *     a published document as a *feed post*: the real feed row (`PostFeedItem`) over
 *     a record that holds the file and a post view carrying **no** embed at all —
 *     which is what the app view answers for a document, and what made a published
 *     document read as an empty post
 *
 * Why a fixture at all: writing a page's material is the *composer's* path
 * (`onChangePageMaterial` is what makes a reader an author's), and the composer is
 * a signed-in screen with a picked file — three things a browser gate would have
 * to stage before it could measure a card. The fixture stages the two that are
 * not the subject (a session, a file) and renders the one that is.
 */
import {useMemo, useState} from 'react'
import {Text, TouchableOpacity, View} from 'react-native'
import {
  type AppBskyFeedDefs,
  type AppBskyFeedPost,
  moderatePost,
  type ModerationOpts,
} from '@atproto/api'
import {useRoute} from '@react-navigation/native'

import {buildDocumentEmbedRecord} from '#/lib/document/publish'
import {
  DOCUMENT_EMBED_VIEW_TYPE,
  type DocumentEmbedView,
} from '#/lib/document/types'
import {moderationOptsOverrideContext} from '#/state/preferences/moderation-opts'
import {PostFeedItem} from '#/view/com/posts/PostFeedItem'
import * as Layout from '#/components/Layout'
import {PdfFullscreen} from '#/components/Post/Embed/document/PdfFullscreen.web'
import {minimalPdf} from './minimalPdf'

/** The fixture document's page count, and what the reader is told to expect. */
const PAGES = 2

/**
 * Where every blob in this fixture lives.
 *
 * The reader is handed this DID with its embed, and a *post* carries it on the
 * record the card reads: the address a document's bytes are fetched from is
 * `did` + `cid` (see `#/lib/document/util`), and a fixture whose two halves
 * disagreed about the did would fetch from a repo the route never answers for.
 */
const FIXTURE_DID = 'did:plc:documentfixture'

/**
 * The bytes, built once for the page's lifetime.
 *
 * `pdf.web` copies them before handing them to pdf.js (which transfers the buffer
 * to its worker), so one array can be opened, closed and opened again — and the
 * parsed document is cached by cache key anyway, so a second mount is a cache hit
 * rather than a second parse.
 *
 * Two documents rather than one resized: the shape is read from the pages when
 * the document loads (`getPdfPageRatio`), so a landscape reader has to be opened
 * over a landscape *file*.
 */
const PORTRAIT_BYTES = minimalPdf(PAGES)
const LANDSCAPE_BYTES = minimalPdf(PAGES, {width: 400, height: 300})

const EMBED: DocumentEmbedView = {
  $type: DOCUMENT_EMBED_VIEW_TYPE,
  cid: 'bafyreidocumentfixture',
  mimeType: 'application/pdf',
  title: 'Fixture worksheet',
  pageCount: PAGES,
}

/**
 * A flag, from either the route's params or the query string — the same contract
 * the other fixtures use, since a gate sets a URL rather than wiring a deep link.
 */
function useParam(name: string): string | null {
  const route = useRoute()
  // Read off the route's own object rather than a fresh `?? {}`: the params are
  // the memo's dependency, and a new empty object each render would be a new flag.
  const params = route.params as Record<string, unknown> | undefined
  return useMemo(() => {
    const fromQuery =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get(name)
        : null
    const asked = params?.[name] ?? fromQuery ?? null
    return typeof asked === 'string' ? asked : null
  }, [params, name])
}

function useFlag(name: string): boolean {
  const asked = useParam(name)
  return asked === '1' || asked === 'true'
}

/**
 * The document a published *post* carries, built by the publisher's own builder.
 *
 * That builder is not a nicety here: it is what turns the upload's link into a real
 * `BlobRef`, which is the shape a record read back *through the agent* holds — and a
 * fixture that hand-wrote the JSON instead would be a shape no server hands the app,
 * which is exactly how a card that could not read a `BlobRef`'s cid passed a gate here
 * and drew nothing in the feed.
 *
 * The title is the field the gate holds the card to: the row is handed no embed at
 * all, so a card naming this document can only have been built from this record.
 */
const POST_BYTES = minimalPdf(PAGES, {width: 300, height: 400})
const POST_DOCUMENT = buildDocumentEmbedRecord({
  document: {
    blob: {
      $type: 'blob',
      // A real CID — the builder parses it — so the address the card fetches from is
      // the one the gate expects to see asked for.
      ref: {
        $link: 'bafkreihcknjtzkkusw6572osfubs7lq4rfrweo37jqdo3grxah2cuetbla',
      },
      mimeType: 'application/pdf',
      size: POST_BYTES.length,
    },
    mimeType: 'application/pdf',
    title: 'Evaporation fixture worksheet',
    pageCount: PAGES,
    fileSize: POST_BYTES.length,
  },
  alt: 'Two pages of a fixture worksheet',
  audience: {level: 'Level_A2', subject: 'Maths'},
})

/**
 * The published post itself, in the two halves a real one has.
 *
 * The record holds the file (the first half a publisher writes); the post view holds
 * **no** `embed`, which is the second half the app view answers for a document — no
 * view was built for it, so there is nothing on the post but the record. That is the
 * shape that read as an empty post, and it is the shape this fixture reproduces.
 */
function fixtureDocumentPost(): AppBskyFeedDefs.PostView {
  return {
    $type: 'app.bsky.feed.defs#postView',
    uri: `at://${FIXTURE_DID}/app.bsky.feed.post/fixture-document`,
    cid: 'bafyfixturedocumentpost',
    author: {
      did: FIXTURE_DID,
      handle: 'fixture-creator.test',
      displayName: 'Fixture Creator',
    },
    record: {
      $type: 'app.bsky.feed.post',
      text: '',
      createdAt: '2026-10-04T09:04:35.146Z',
      embed: POST_DOCUMENT,
    },
    embed: undefined,
    labels: [],
    indexedAt: '2026-10-04T09:04:35.146Z',
    likeCount: 0,
    replyCount: 0,
    repostCount: 0,
    quoteCount: 0,
    bookmarkCount: 0,
  } as unknown as AppBskyFeedDefs.PostView
}

/**
 * A moderation context for the fixture row.
 *
 * The feed's own provider reads preferences over the network, which a signed-out
 * browser cannot do — and a row that never renders is not a row that can be measured.
 * So the override context the devtools use is the honest way in: the real
 * `PostFeedItem` renders against real moderation decisions, over a context that says
 * "this viewer has no preferences", which is true of this browser.
 */
const FIXTURE_MODERATION_OPTS: ModerationOpts = {
  userDid: undefined,
  prefs: {
    adultContentEnabled: false,
    labels: {},
    labelers: [],
    mutedWords: [],
    hiddenPosts: [],
  },
  labelDefs: {},
}

export function DocumentFixturesScreen() {
  const fixture = useParam('fixture')
  const editor = useFlag('editor')
  const landscape = useFlag('landscape')
  const [open, setOpen] = useState(true)
  /*
   * Memoised because `usePdfDocument` re-runs on the source's identity: a fresh
   * object each render would be a fresh open of the same document (the parse is
   * cached, the effect is not). The cache key is part of the identity because the
   * document cache is keyed by it: one key for two shapes of file would hand a
   * reader the parse of the other one.
   */
  const preview = useMemo(
    () => ({
      cacheKey: landscape ? 'fixture:worksheet:landscape' : 'fixture:worksheet',
      bytes: landscape ? LANDSCAPE_BYTES : PORTRAIT_BYTES,
    }),
    [landscape],
  )

  /*
   * A published document, as a post.
   *
   * The real row — `PostFeedItem` — rather than the card on its own, because the bug
   * this surface exists for was never in the card: the row was handed no embed at all.
   * A fixture that rendered the card directly could not have caught that, and could
   * not catch it coming back.
   *
   * The branch sits *after* the screen's own hooks, so the reader and the row are two
   * renders of one component with one set of hooks: a return above that `useMemo`
   * would make every hook below it conditional.
   */
  if (fixture === 'post') {
    const post = fixtureDocumentPost()
    return (
      <moderationOptsOverrideContext.Provider value={FIXTURE_MODERATION_OPTS}>
        <Layout.Screen>
          <Layout.Center style={{flex: 1}}>
            {/*
              A published document, page one. It is drawn from the record — the post
              carries no embed — so what the gate sees here is the card the record
              built, over the file the route serves for the blob the record names.
            */}
            <PostFeedItem
              post={post}
              record={post.record as AppBskyFeedPost.Record}
              reason={undefined}
              feedContext={undefined}
              reqId={undefined}
              moderation={moderatePost(post, FIXTURE_MODERATION_OPTS)}
              parentAuthor={undefined}
              showReplyTo={false}
              rootPost={post}
              hideTopBorder
            />
          </Layout.Center>
        </Layout.Screen>
      </moderationOptsOverrideContext.Provider>
    )
  }

  return (
    <Layout.Screen>
      <View style={{flex: 1, alignItems: 'center', justifyContent: 'center'}}>
        <Text>Document reader fixture</Text>
        {!open && (
          <TouchableOpacity
            accessibilityRole="button"
            onPress={() => setOpen(true)}
            testID="documentFixtureOpen">
            <Text>Open the reader</Text>
          </TouchableOpacity>
        )}
      </View>
      {open && (
        <PdfFullscreen
          embed={EMBED}
          authorDid={FIXTURE_DID}
          preview={preview}
          numPagesHint={PAGES}
          materialState="none"
          startEditorPage={editor ? 1 : undefined}
          onChangePageMaterial={() => {}}
          onClose={() => setOpen(false)}
        />
      )}
    </Layout.Screen>
  )
}
